import { GoogleGenAI } from "@google/genai";

// esbuild will replace `process.env.API_KEY` with its value at build time.
const apiKey = process.env.API_KEY;

/**
 * Analyzes financial data using the Gemini API to provide business insights.
 * @param summary - An object containing key financial metrics.
 * @param timeframe - The time period for the analysis (e.g., 'DAILY', 'MONTHLY').
 * @returns A string containing the AI-generated analysis or an error message.
 */
export const analyzeFinancialData = async (summary: any, timeframe: string): Promise<string> => {
  // Gracefully handle the missing API key at the point of use, not at app startup.
  if (!apiKey) {
    console.warn("Gemini API key is not configured. AI features are disabled.");
    return "AI insight is unavailable. The Google Gemini API key has not been configured in the application's build environment.";
  }

  // Initialize the client here, only when the function is called and the key exists.
  const ai = new GoogleGenAI({ apiKey });

  // Select a model suitable for complex reasoning tasks like financial analysis as per guidelines.
  const model = 'gemini-3-pro-preview';

  const prompt = `
    Analyze the following financial summary for a small medical store for the ${timeframe.toLowerCase()} period.
    Provide a concise, actionable business insight based on these numbers.
    Do not just list the numbers back. Explain what they mean for the business's health.
    Focus on profitability, cash flow, and any potential red flags.
    Keep the analysis to 2-3 short sentences. Be professional and encouraging.

    Financial Summary:
    - Total Sales Revenue: RS. ${summary.sales.toLocaleString()}
    - Cost of Goods Sold: RS. ${summary.costOfGoods.toLocaleString()}
    - Total Procurement Cost (New Inventory): RS. ${summary.procurement.toLocaleString()}
    - Total Overhead Expenses: RS. ${summary.overhead.toLocaleString()}
    - Net Profit: RS. ${summary.netProfit.toLocaleString()}
    - Current Inventory Valuation (at cost): RS. ${summary.inventoryValuation.toLocaleString()}
    - Top Vendor by spending: ${summary.vendorPerformance.length > 0 ? `${summary.vendorPerformance[0].name} (RS. ${summary.vendorPerformance[0].totalCost.toLocaleString()})` : 'N/A'}

    Example output: "Your sales are strong this ${timeframe.toLowerCase()}, but high procurement costs are impacting your net profit. Consider negotiating with your top supplier or exploring alternatives to improve margins."
  `;

  try {
    // Call the Gemini API using the recommended `generateContent` method.
    const response = await ai.models.generateContent({
      model: model,
      contents: prompt,
    });
    
    // Use the .text property to extract the response string, as per guidelines.
    const analysisText = response.text;
    if (analysisText) {
        return analysisText;
    } else {
        return "Could not generate analysis. The response was empty.";
    }

  } catch (error) {
    console.error("Error analyzing financial data with Gemini:", error);
    return "Failed to generate AI insight due to an API error. Please check your connection and API key configuration.";
  }
};